// src/utils/allegatiCommento.js
// Foto e file nei commenti di attività e sottoattività (W440). Ogni allegato sta nel commento
// che lo accompagna (campo «allegati» del documento in «comments»), così resta legato a quel
// messaggio: chi apre l'attività vede chi l'ha mandato, quando e con quale testo. I file vanno
// nell'archivio (Storage) in attivita/{organizzazione}/{attività}/…: regole Storage v4.
// Le foto si rimpiccioliscono prima di partire (lato lungo 1600 px): dal cantiere, con poca
// rete, una foto di 6 MB diventa 300 KB e si legge benissimo.
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase";

export const MAX_MB = 20;
export const MAX_FILE_PER_COMMENTO = 10;
// Stessi tipi ammessi dalle regole Storage (tipoAllegatoAmmesso): niente HTML o script.
const ESTENSIONI = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "zip"];
export const ACCETTA_FILE = ESTENSIONI.map(e => "." + e).join(",");
const MIME = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif", heic: "image/heic", heif: "image/heif",
  pdf: "application/pdf", doc: "application/msword", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint", pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain", csv: "text/csv", zip: "application/zip",
};

export const estensione = (nome) => (String(nome || "").toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || "";
/** Tipo da dichiarare all'archivio: dall'estensione (alcuni telefoni non lo danno o lo danno sbagliato). */
export const tipoContenuto = (nome, tipoDichiarato = "") => MIME[estensione(nome)] || tipoDichiarato || "application/octet-stream";
export const eImmagine = (a) => /^image\//.test(a?.contentType || tipoContenuto(a?.nome)) || a?.tipo === "foto";

/** "" se il file si può allegare, altrimenti il motivo. */
export function controllaFile(file) {
  if (!file) return "File non valido";
  if (!ESTENSIONI.includes(estensione(file.name))) return `«${file.name}»: tipo di file non ammesso (sì foto, PDF, Word, Excel, PowerPoint, testo, CSV, ZIP)`;
  if (file.size > MAX_MB * 1024 * 1024) return `«${file.name}» supera ${MAX_MB} MB`;
  return "";
}

/** Nome sicuro per il percorso nell'archivio (lettere, numeri, punto, trattino). */
export function nomeSicuro(nome) {
  const ext = estensione(nome);
  const base = String(nome || "file").replace(/\.[^.]+$/, "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "file";
  return ext ? `${base}.${ext}` : base;
}

export function dimensioneLeggibile(byte) {
  const n = Number(byte) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toLocaleString("it-IT", { maximumFractionDigits: 1 })} MB`;
}

export function iconaFile(nome) {
  const e = estensione(nome);
  if (e === "pdf") return "📕";
  if (["doc", "docx", "txt"].includes(e)) return "📝";
  if (["xls", "xlsx", "csv"].includes(e)) return "📊";
  if (["ppt", "pptx"].includes(e)) return "📽️";
  if (e === "zip") return "🗜️";
  if (["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"].includes(e)) return "🖼️";
  return "📎";
}

// Rimpicciolisce una foto (JPEG/PNG/WebP) in un JPEG; se il browser non la sa leggere (es.
// HEIC fuori da Safari) parte l'originale.
function rimpicciolisci(file) {
  return new Promise((resolve) => {
    if (!/\.(jpe?g|png|webp)$/i.test(file.name) || typeof document === "undefined") { resolve(null); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.onload = () => {
      const lato = 1600;
      let { width: w, height: h } = img;
      if (w > lato || h > lato) { if (w >= h) { h = Math.round(h * lato / w); w = lato; } else { w = Math.round(w * lato / h); h = lato; } }
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      c.toBlob(b => resolve(b && b.size < file.size ? b : null), "image/jpeg", 0.8);
    };
    img.src = url;
  });
}

/** Carica i file e restituisce gli allegati da salvare nel commento. */
export async function caricaAllegati(files, { orgId, taskId }) {
  const fuori = [];
  try {
    for (const [i, f] of Array.from(files).entries()) {
      const piccola = await rimpicciolisci(f);
      const nome = piccola ? f.name.replace(/\.[^.]+$/, "") + ".jpg" : f.name;
      const contentType = piccola ? "image/jpeg" : tipoContenuto(f.name, f.type);
      const path = `attivita/${orgId}/${taskId}/${Date.now()}_${i}_${nomeSicuro(nome)}`;
      const r = ref(storage, path);
      await uploadBytes(r, piccola || f, { contentType });
      fuori.push({ nome, path, url: await getDownloadURL(r), contentType, tipo: /^image\//.test(contentType) ? "foto" : "file", dimensione: (piccola || f).size || 0 });
    }
  } catch (e) {
    // se uno non parte, si tolgono quelli già caricati: nessun file orfano nell'archivio
    await eliminaAllegati(fuori);
    throw e;
  }
  return fuori;
}

/** Toglie i file dall'archivio (es. quando si elimina il commento). Non si ferma sugli errori. */
export async function eliminaAllegati(allegati = []) {
  await Promise.all((allegati || []).filter(a => a?.path).map(a => deleteObject(ref(storage, a.path)).catch(() => {})));
}
